import { Workspace } from "ts-interfaces";

/**
 * Shared avatar/identity helpers for workspaces. Used by the persistent
 * workspace switcher pill (Bar) and any menu rows that render a workspace
 * avatar, so the swatch color + initials are consistent everywhere.
 *
 * Disambiguation (Chris's #1): a single uppercase initial made same-initial
 * teams ("Acme" vs "Anvil" → both "A") indistinguishable. We pair a
 * deterministic per-firm color with two-letter initials so two teams are
 * always visually distinct even before you read the name.
 */

/** Fixed neutral grey for the Personal workspace (it renders a person icon,
 * not initials, so it never collides with a team). */
export const PERSONAL_AVATAR_COLOR = "#5f6368";

/** Stable swatch color hashed from an id (team members use it too). */
export const hashColor = (seed: string): string => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  // 30% lightness keeps white text at WCAG AA (4.5:1) for every hue.
  return `hsl(${hash % 360}, 55%, 30%)`;
};

/**
 * Deterministic, stable swatch color for a team workspace, hashed from the
 * firm id (not the name) so renaming a team keeps its color and two teams
 * never share one by coincidence. Personal is always the neutral grey.
 */
export const workspaceColor = (w: Workspace): string =>
  w.kind === "personal" ? PERSONAL_AVATAR_COLOR : hashColor(w.firmId);

/**
 * 1–2 letter initials for a team label. Word-initials when the label has
 * multiple words ("Acme Design Studio" → "AD"), otherwise the first two
 * letters of a single word ("Anvil" → "AN").
 */
export const workspaceInitials = (label: string): string => {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "T";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};
